import { createSelector, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  dispatchLoad,
  fetchDispatchQueue,
  loadDispatchOrder,
  retryFailedDispatch,
  scanDispatchCode,
} from './operatorDispatchThunks';
import {
  DISPATCH_OUTCOME,
  LABEL_MATCH,
  RECHECK,
  emptyEntry,
  isDispatchBusy,
  loadSummary,
  scanAdds,
  shipmentRepeatLabel,
} from './operatorDispatchUtils';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * Dispatch on the floor: the orders ready to leave, and the load being put
 * up the truck. The load is the operator's work of many scans — thirty
 * orders, say —, so it survives a reload; everything else is asked again.
 */
const initialQueue = {
  items: [],
  count: 0,
  /** `[{code, label, count}]`: the queue's couriers, for the filter. */
  couriers: [],
  /** The courier the queue is filtered by; `''` for all. */
  courier: '',
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
  /** When its first page last came, by the phone's clock. */
  receivedAt: null,
};

/**
 * `orders`: by order id, `{row, match, code, scannedItemIds, shipmentCount,
 * error}` (see `emptyEntry`); `orderIds`: the one last touched first;
 * `warehouseId`: the warehouse it is being loaded in.
 */
const initialLoad = { warehouseId: null, orderIds: [], orders: {} };

const initialState = {
  queue: initialQueue,
  load: initialLoad,
  scan: { lastCode: null },
  /** The `requestId` of the read being worked out. */
  scanning: null,
  /** `{orderId, code}` of the last shipment label read, for «+1 bulto». */
  lastShipment: null,
  /** The `requestId` of the failed orders being read again. */
  retrying: null,
  /** A dispatch on its way. */
  pending: false,
  /** `{type, arg, key}` of the last dispatch sent: the only one whose answer it waits for. */
  lastWrite: null,
  /** How the last dispatch went: `{results, dispatched, already, failed}`. */
  result: null,
};

/** Puts an order first in the load, as the one last touched. */
const putFirst = (load, entry) => {
  const id = entry.row.id;
  load.orders[id] = entry;
  load.orderIds = [id, ...load.orderIds.filter((other) => other !== id)];
};

const removeOrder = (state, orderId) => {
  if (!state.load.orders[orderId]) return;
  delete state.load.orders[orderId];
  state.load.orderIds = state.load.orderIds.filter((id) => id !== orderId);
  if (state.lastShipment?.orderId === orderId) state.lastShipment = null;
};

/** Whether an answer is to the dispatch the station waits for. */
const isAwaited = (state, action) => action.meta.requestId === state.lastWrite?.key;

const operatorDispatchSlice = createSlice({
  name: 'operatorDispatch',
  initialState,
  reducers: {
    /**
     * The station opened in a warehouse. A load kept from another one — the
     * context sent the operator elsewhere without choosing — is not this
     * one's to send. The result of the last dispatch was said already.
     *
     * @param {{payload: number}} action - The warehouse in use.
     */
    dispatchStationEntered: (state, action) => {
      if (state.load.warehouseId !== action.payload) state.load = { ...initialLoad, warehouseId: action.payload };
      state.result = null;
    },
    /** @param {{payload: number}} action - The order taken out of the load: its items come down. */
    dispatchLoadOrderRemoved: (state, action) => {
      if (isDispatchBusy(state)) return;
      removeOrder(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Choosing the same warehouse again changes nothing of what is up the truck.
      .addCase(operatorWarehouseSelected, (state, action) =>
        action.payload === state.load.warehouseId
          ? { ...initialState, load: state.load }
          : { ...initialState, load: { ...initialLoad, warehouseId: action.payload } }
      )

      .addCase(fetchDispatchQueue.pending, (state, action) => {
        const courier = action.meta.arg?.courier ?? '';
        if (courier !== state.queue.courier) state.queue.items = [];
        state.queue.courier = courier;
        state.queue.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.queue.loading = true;
        state.queue.error = null;
      })
      .addCase(fetchDispatchQueue.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.queue.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.queue.loading = false;
        state.queue.error = null;
        state.queue.page = page;
        state.queue.items = page === 1 ? results : [...state.queue.items, ...results];
        state.queue.count = action.payload.count ?? state.queue.items.length;
        state.queue.couriers = action.payload.couriers || [];
        state.queue.hasMore = Boolean(action.payload.next);
        if (page === 1) state.queue.receivedAt = action.meta.receivedAt;
      })
      .addCase(fetchDispatchQueue.rejected, (state, action) => {
        // A refresh in the background fails in silence: the list stays as it was, and the next one tries again.
        if (action.meta.requestId !== state.queue.requestId || isBackgroundRequest(action)) return;
        state.queue.loading = false;
        state.queue.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanDispatchCode.pending, (state, action) => {
        state.scanning = action.meta.requestId;
      })
      .addCase(scanDispatchCode.fulfilled, (state, action) => {
        if (state.scanning === action.meta.requestId) state.scanning = null;
        const { outcome, code, warehouseId } = action.payload;
        // Read for a load another warehouse replaced meanwhile.
        if (warehouseId !== state.load.warehouseId) return;
        state.scan.lastCode = acceptedCode(action);
        putFirst(state.load, outcome.entry);
        state.lastShipment = outcome.entry.match === LABEL_MATCH.SHIPMENT ? { orderId: outcome.entry.row.id, code } : null;
        // A new load starts once the last one left whole; failures stay on screen until they are seen to.
        if (scanAdds(outcome) && loadSummary(state.load).failed.length === 0) state.result = null;
      })
      .addCase(scanDispatchCode.rejected, (state, action) => {
        if (state.scanning === action.meta.requestId) state.scanning = null;
      })

      // A tapped order is read again as a scan is, and the station waits on it the same way.
      .addCase(loadDispatchOrder.pending, (state, action) => {
        state.scanning = action.meta.requestId;
      })
      .addCase(loadDispatchOrder.fulfilled, (state, action) => {
        if (state.scanning === action.meta.requestId) state.scanning = null;
        const { outcome, warehouseId } = action.payload;
        if (warehouseId !== state.load.warehouseId) return;
        putFirst(state.load, outcome.entry);
        if (scanAdds(outcome) && loadSummary(state.load).failed.length === 0) state.result = null;
      })
      .addCase(loadDispatchOrder.rejected, (state, action) => {
        if (state.scanning === action.meta.requestId) state.scanning = null;
      })

      .addCase(dispatchLoad.pending, (state, action) => {
        state.pending = true;
        state.result = null;
        state.lastWrite = { type: dispatchLoad.typePrefix, arg: intentOf(action.meta.arg), key: action.meta.requestId };
      })
      // What left leaves the load, whichever dispatch says it; a failure stays, marked.
      .addCase(dispatchLoad.fulfilled, (state, action) => {
        const results = action.payload.results || [];
        results.forEach((result) => {
          if (result.outcome === DISPATCH_OUTCOME.FAILED) {
            const entry = state.load.orders[result.order_id];
            if (entry) entry.error = { message: result.error || 'No se pudo despachar.' };
          } else {
            removeOrder(state, result.order_id);
          }
        });
        if (!isAwaited(state, action)) return;
        state.pending = false;
        state.lastWrite = null;
        state.result = {
          results,
          dispatched: action.payload.dispatched ?? 0,
          already: action.payload.already ?? 0,
          failed: action.payload.failed ?? 0,
        };
      })
      .addCase(dispatchLoad.rejected, (state, action) => {
        if (action.meta.condition || !isAwaited(state, action)) return;
        state.pending = false;
        if (!isRetryable(action.payload)) state.lastWrite = null;
      })

      .addCase(retryFailedDispatch.pending, (state, action) => {
        state.retrying = action.meta.requestId;
        state.result = null;
      })
      .addCase(retryFailedDispatch.fulfilled, (state, action) => {
        if (state.retrying === action.meta.requestId) state.retrying = null;
        action.payload.checks.forEach((check) => {
          const entry = state.load.orders[check.orderId];
          if (!entry) return;
          if (check.status === RECHECK.READY) {
            Object.assign(entry, { row: check.row, error: null });
          } else if (check.status === RECHECK.RESCAN) {
            state.load.orders[check.orderId] = emptyEntry(check.row, entry.match, entry.code);
          } else if (check.status === RECHECK.REFUSED) {
            entry.error = { message: check.message };
          }
        });
      })
      .addCase(retryFailedDispatch.rejected, (state, action) => {
        if (state.retrying === action.meta.requestId) state.retrying = null;
      });
  },
});

export const { dispatchStationEntered, dispatchLoadOrderRemoved } = operatorDispatchSlice.actions;

export const selectDispatchLoad = (state) => state.operatorDispatch.load;

/** The load in numbers; see `loadSummary`. */
export const selectDispatchSummary = createSelector([selectDispatchLoad], loadSummary);

/** Whether a read code is welcome right now. */
export const selectDispatchAccepting = (state) => !isDispatchBusy(state.operatorDispatch);

/** The «+1 bulto» of the order last read by its shipment label, while it has items to count; `null` otherwise. */
export const selectShipmentRepeatLabel = (state) => {
  const { lastShipment, load } = state.operatorDispatch;
  return lastShipment ? shipmentRepeatLabel(load.orders[lastShipment.orderId]) : null;
};

/**
 * Only the load survives a reload: losing thirty orders scanned is the worst
 * a dropped tab can do here. The rest is asked again, or was about a moment.
 */
export const dispatchPersistConfig = {
  key: 'operatorDispatch',
  storage,
  whitelist: ['load'],
};

export default persistReducer(dispatchPersistConfig, operatorDispatchSlice.reducer);
